import type { DateWindow } from "@nexus/contracts";
import type { DashboardCreativesRequest } from "@/lib/queries/dashboard.queries";
import type { DrilldownSearch } from "@/features/drilldown/lib/drilldown-search";
import type { CompetitorFilters, MyBrandFilters } from "../types/dashboard.types";
import {
  appliedFiltersToExploreSearch,
  pickExploreDateSearch,
  type DashboardDateReturn,
} from "./explore-search";
import { presetIdToLegacyToken, resolvePresetWindow, type RollingPresetId } from "./explore-date-presets";

export type CompetitorCreativeRowId = "newest" | "mostDuplicated" | "longestRunning";

export interface CompetitorCreativeRow {
  id: CompetitorCreativeRowId;
  /** Drilldown's competitor sort — orders the row's creatives. */
  sortMode: "Date" | "Assets" | "Days";
  sortDirection: "asc" | "desc";
  /** A window that ignores the header range. Rows without one follow the header. */
  fixedPresetId?: RollingPresetId;
}

/** Top to bottom, in the order the Competitors view shows them. */
export const COMPETITOR_CREATIVE_ROWS: readonly CompetitorCreativeRow[] = [
  { id: "newest", sortMode: "Date", sortDirection: "desc", fixedPresetId: "last_7_days" },
  { id: "mostDuplicated", sortMode: "Assets", sortDirection: "desc" },
  { id: "longestRunning", sortMode: "Days", sortDirection: "desc" },
];

/** Creatives shown per row; Drilldown continues the list. */
export const CREATIVE_ROW_SIZE = 8;

/** The Drilldown request behind one row: its sort, and its own window or the header's. */
export function buildCreativeRowRequest(
  row: CompetitorCreativeRow,
  headerDateRange: DashboardCreativesRequest["dateRange"],
  headerWindow: DateWindow,
  today: Date = new Date(),
): DashboardCreativesRequest {
  const fixedWindow = row.fixedPresetId ? resolvePresetWindow(row.fixedPresetId, today) : null;
  const fixedToken = row.fixedPresetId ? presetIdToLegacyToken(row.fixedPresetId) : undefined;
  const window =
    fixedWindow && fixedToken
      ? { dateRange: fixedToken, startDate: fixedWindow.start, endDate: fixedWindow.end }
      : { dateRange: headerDateRange, startDate: headerWindow.startDate, endDate: headerWindow.endDate };

  return { sortMode: row.sortMode, sortDirection: row.sortDirection, ...window };
}

/**
 * The `/drilldown` search a row's "Explore in Drilldown" opens: the applied
 * competitor filters plus the row's sort, and its fixed window when it has one.
 */
export function buildCreativeRowDrilldownSearch(
  row: CompetitorCreativeRow,
  request: DashboardCreativesRequest,
  appliedCompetitor: CompetitorFilters,
  appliedMyBrand: MyBrandFilters,
): DrilldownSearch {
  const search: DrilldownSearch = {
    ...appliedFiltersToExploreSearch("competitors", appliedCompetitor, appliedMyBrand, {
      persistCustomWindow: true,
    }),
    sortMode: row.sortMode,
    sortDirection: row.sortDirection,
  };
  if (!row.fixedPresetId) return search;

  return {
    ...search,
    presetId: row.fixedPresetId,
    dateRange: request.dateRange,
    customStart: undefined,
    customEnd: undefined,
    startDate: request.startDate,
    endDate: request.endDate,
  };
}

/**
 * What to put back on the Dashboard after a row opens Drilldown on its own window.
 * Null for rows that keep the header range, since they leave the date alone.
 */
export function buildDashboardDateReturn(
  row: CompetitorCreativeRow,
  drilldownSearch: DrilldownSearch,
  appliedCompetitor: CompetitorFilters,
  appliedMyBrand: MyBrandFilters,
): DashboardDateReturn | null {
  if (!row.fixedPresetId) return null;

  return {
    dashboard: pickExploreDateSearch(
      appliedFiltersToExploreSearch("competitors", appliedCompetitor, appliedMyBrand, {
        persistCustomWindow: true,
      }),
    ),
    row: pickExploreDateSearch(drilldownSearch),
  };
}
